import React from "react";
import { ArrowUpRight } from "lucide-react";
import { CATEGORIES } from "../../lib/catalog";

function CategoryCard({ cat, index }) {
    const number = String(index + 1).padStart(2, "0");

    if (cat.tone === "dark") {
        return (
            <a
                href="#inquiry"
                className={`cat-card relative overflow-hidden bg-[#0C4A6E] text-white p-8 md:p-10 flex flex-col justify-between min-h-[340px] md:min-h-[420px] ${cat.span} border border-transparent`}
                data-testid={`category-${cat.slug}`}
            >
                {cat.image && (
                    <img
                        src={cat.image}
                        alt={cat.name}
                        className="absolute inset-0 w-full h-full object-cover opacity-30 mix-blend-luminosity"
                    />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0C4A6E] via-[#0C4A6E]/85 to-[#0C4A6E]/60" />
                <div className="relative flex items-start justify-between gap-4">
                    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-sky-200">
                        {number} / {String(CATEGORIES.length).padStart(2, "0")}
                    </span>
                    <ArrowUpRight
                        className="cat-arrow text-sky-200"
                        size={22}
                        strokeWidth={1.5}
                    />
                </div>
                <div className="relative">
                    <h3 className="font-cabinet font-bold text-3xl md:text-4xl leading-tight tracking-tight">
                        {cat.name}
                    </h3>
                    <p className="mt-3 text-sky-100/90 text-sm md:text-base">
                        {cat.tagline}
                    </p>
                    <ul className="mt-6 space-y-2 text-sm text-sky-50/90">
                        {cat.items.slice(0, 5).map((it) => (
                            <li key={it} className="flex gap-2">
                                <span className="text-sky-300">—</span>
                                <span>{it}</span>
                            </li>
                        ))}
                        {cat.items.length > 5 && (
                            <li className="text-xs font-mono uppercase tracking-[0.18em] text-sky-200/70 pt-1">
                                +{cat.items.length - 5} more
                            </li>
                        )}
                    </ul>
                </div>
            </a>
        );
    }

    if (cat.tone === "earth") {
        return (
            <a
                href="#inquiry"
                className={`cat-card relative overflow-hidden bg-[#4D7C0F] text-white p-8 md:p-10 flex flex-col justify-between min-h-[340px] md:min-h-[420px] ${cat.span} border border-transparent`}
                data-testid={`category-${cat.slug}`}
            >
                {cat.image && (
                    <img
                        src={cat.image}
                        alt={cat.name}
                        className="absolute inset-0 w-full h-full object-cover opacity-30 mix-blend-luminosity"
                    />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#3f6a06] via-[#4D7C0F]/85 to-[#4D7C0F]/60" />
                <div className="relative flex items-start justify-between gap-4">
                    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-lime-100">
                        {number} / {String(CATEGORIES.length).padStart(2, "0")}
                    </span>
                    <ArrowUpRight
                        className="cat-arrow text-lime-100"
                        size={22}
                        strokeWidth={1.5}
                    />
                </div>
                <div className="relative">
                    <h3 className="font-cabinet font-bold text-3xl md:text-4xl leading-tight tracking-tight">
                        {cat.name}
                    </h3>
                    <p className="mt-3 text-lime-100/95 text-sm md:text-base">
                        {cat.tagline}
                    </p>
                    <ul className="mt-6 space-y-2 text-sm text-lime-50/90">
                        {cat.items.slice(0, 4).map((it) => (
                            <li key={it} className="flex gap-2">
                                <span className="text-lime-200">—</span>
                                <span>{it}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </a>
        );
    }

    return (
        <a
            href="#inquiry"
            className={`cat-card group relative bg-white border border-stone-200 flex flex-col min-h-[380px] md:min-h-[460px] overflow-hidden ${cat.span}`}
            data-testid={`category-${cat.slug}`}
        >
            {cat.image && (
                <div className="relative h-40 md:h-48 overflow-hidden bg-stone-100">
                    <img
                        src={cat.image}
                        alt={cat.name}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-white/40 via-transparent to-transparent" />
                    <div className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.2em] text-white bg-[#0C4A6E]/80 backdrop-blur px-2 py-1">
                        {number}
                    </div>
                </div>
            )}
            <div className="flex-1 flex flex-col justify-between p-7 md:p-8">
                {!cat.image && (
                    <div className="flex items-start justify-between gap-4 mb-6">
                        <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-500">
                            {number}
                        </span>
                        <ArrowUpRight
                            className="cat-arrow text-stone-500"
                            size={20}
                            strokeWidth={1.5}
                        />
                    </div>
                )}
                <div>
                    <div className="flex items-start justify-between gap-4">
                        <h3 className="font-cabinet font-bold text-2xl md:text-[26px] leading-tight tracking-tight text-ink">
                            {cat.name}
                        </h3>
                        {cat.image && (
                            <ArrowUpRight
                                className="cat-arrow text-[#0C4A6E] shrink-0 mt-1"
                                size={20}
                                strokeWidth={1.5}
                            />
                        )}
                    </div>
                    <p className="mt-2 text-stone-600 text-sm">{cat.tagline}</p>
                    <ul className="mt-5 space-y-1.5 text-sm text-stone-700">
                        {cat.items.slice(0, 3).map((it) => (
                            <li key={it} className="flex gap-2">
                                <span className="text-[#4D7C0F]">—</span>
                                <span>{it}</span>
                            </li>
                        ))}
                        {cat.items.length > 3 && (
                            <li className="text-xs font-mono uppercase tracking-[0.18em] text-stone-400 pt-1">
                                +{cat.items.length - 3} more SKUs
                            </li>
                        )}
                    </ul>
                </div>
            </div>
        </a>
    );
}

export default function Categories() {
    return (
        <section
            id="categories"
            className="py-24 md:py-32"
            data-testid="categories-section"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid md:grid-cols-12 gap-8 mb-14 md:mb-20">
                    <div className="md:col-span-4">
                        <div className="overline mb-4">03 / Categories</div>
                        <h2 className="font-cabinet font-bold text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
                            Ten product verticals.
                            <br />
                            <span className="italic font-normal text-stone-500">
                                One export partner.
                            </span>
                        </h2>
                    </div>
                    <div className="md:col-span-7 md:col-start-6 self-end">
                        <p className="text-lg text-stone-700 leading-relaxed">
                            From allopathic molecules to AYUSH classicals,
                            cosmetic private-label, nutraceuticals, hospital
                            equipment and turnkey infrastructure — we source,
                            document and deliver against strict regulatory and
                            quality standards.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6">
                    {CATEGORIES.map((cat, i) => (
                        <CategoryCard key={cat.slug} cat={cat} index={i} />
                    ))}
                </div>
            </div>
        </section>
    );
}
